<template>
  <div
    class="tree-node-row d-flex align-stretch"
    :class="rowRootClasses"
    :data-level="level"
    :data-type="item.type"
  >
    <div v-if="level > 0" class="tree-guides d-flex flex-shrink-0" aria-hidden="true">
      <span
        v-for="guideIndex in level"
        :key="`guide-${guideIndex}`"
        class="tree-guide-col"
        :class="{
          'tree-guide-col--last': guideIndex === level && isLastChild
        }"
      />
    </div>

    <div
      ref="treeLabel"
      class="tree-row-main d-flex align-center flex-grow-1"
      tabindex="0"
      @mousedown="handleNodeMouseDown"
      @click="handleNodeClick"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="handleKeydown"
    >
      <div class="tree-chevron-slot d-flex align-center justify-center flex-shrink-0">
        <v-btn
          v-if="hasChildren"
          icon
          small
          class="tree-expand-btn"
          :aria-label="expanded ? $t('panelPlanning.treeCollapse') : $t('panelPlanning.treeExpand')"
          @click.stop="toggleExpandOnly"
        >
          <v-icon :class="{ 'tree-chevron--open': expanded }"> mdi-chevron-right </v-icon>
        </v-btn>
      </div>

      <v-icon
        small
        class="tree-type-icon flex-shrink-0 mr-2"
        :class="rowMeta.iconClass"
        :color="rowMeta.iconColor"
      >
        {{ rowMeta.icon }}
      </v-icon>

      <div class="tree-row-text flex-grow-1 min-width-0 d-flex align-center">
        <span class="tree-row-name text-truncate" :class="rowMeta.nameClass">
          {{ displayName }}
        </span>
        <v-chip
          v-if="showTypeChip"
          x-small
          label
          outlined
          class="tree-type-chip ml-2 flex-shrink-0"
          :color="typeChipColor"
        >
          {{ typeLabel }}
        </v-chip>
      </div>

      <div
        v-if="hasRowActions"
        class="tree-row-actions d-flex align-center flex-shrink-0"
        @click.stop
      >
        <v-tooltip v-if="canRename" bottom :open-delay="tooltipOpenDelay">
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              class="tree-action-btn"
              v-bind="attrs"
              :aria-label="$t('panelPlanning.menuRename')"
              v-on="on"
              @click.stop="handleRename"
            >
              <v-icon small> mdi-pencil-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('panelPlanning.menuRename') }}</span>
        </v-tooltip>

        <v-tooltip v-if="canMove" bottom :open-delay="tooltipOpenDelay">
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              class="tree-action-btn"
              v-bind="attrs"
              :aria-label="moveMenuLabel"
              v-on="on"
              @click.stop="handleMove"
            >
              <v-icon small>{{ moveActionIcon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ moveMenuLabel }}</span>
        </v-tooltip>

        <v-tooltip v-if="canDelete" bottom :open-delay="tooltipOpenDelay">
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              class="tree-action-btn tree-action-btn--danger"
              v-bind="attrs"
              :aria-label="deleteActionLabel"
              v-on="on"
              @click.stop="handleDelete"
            >
              <v-icon small color="error"> mdi-delete-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ deleteActionLabel }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
  import { PANEL_ACTION_ICONS } from '@/features/panel-planning/utils/panelActionIconRegistry';
  import { getPanelTypeConfig } from '@/features/panel-planning/utils/panelTypeRegistry';
  import { getPanelTreeRowMeta } from '@/features/panel-planning/utils/panelTreeRowMeta';

  /** 맵 툴바(MapToolbarTooltipBlock)와 동일 — hover 즉시 툴팁 */
  const TOOLTIP_OPEN_DELAY_MS = 0;

  export default {
    name: 'TreeNodeRow',
    props: {
      item: { type: Object, required: true },
      level: { type: Number, default: 0 },
      isLastChild: { type: Boolean, default: false },
      hasChildren: { type: Boolean, default: false },
      expanded: { type: Boolean, default: false },
      isSelected: { type: Boolean, default: false },
      selectedSite: { type: Object, default: null },
      focusedNodeId: { type: [String, Number], default: null },
      /** 스크롤 sticky 로 상단에 고정된 String 행 */
      pinned: { type: Boolean, default: false }
    },
    data() {
      return { tooltipOpenDelay: TOOLTIP_OPEN_DELAY_MS };
    },
    computed: {
      typeConfig() {
        return getPanelTypeConfig(this.item.type);
      },
      isFocused() {
        return this.focusedNodeId != null && this.focusedNodeId === this.item.id;
      },
      rowMeta() {
        return getPanelTreeRowMeta(this.item.type, {
          expanded: this.expanded,
          hasChildren: this.hasChildren
        });
      },
      showTypeChip() {
        return this.rowMeta.showTypeChip !== false && Boolean(this.typeLabel);
      },
      typeLabel() {
        return this.typeConfig.labelKey ? this.$t(this.typeConfig.labelKey) : '';
      },
      typeChipColor() {
        return this.typeConfig.color;
      },
      displayName() {
        return (this.item.name || '').replace(/^(Module|String|Array)\s+/i, '');
      },
      canRename() {
        return this.typeConfig.canRename;
      },
      hasRowActions() {
        return this.canRename || this.typeConfig.canMove || this.typeConfig.canDelete;
      },
      canMove() {
        return this.typeConfig.canMove;
      },
      canDelete() {
        return this.typeConfig.canDelete;
      },
      moveMenuLabel() {
        return this.typeConfig.moveMenuLabel ? this.$t(this.typeConfig.moveMenuLabel) : '';
      },
      moveActionIcon() {
        return PANEL_ACTION_ICONS.move;
      },
      deleteActionLabel() {
        return this.typeConfig.deleteLabelKey ? this.$t(this.typeConfig.deleteLabelKey) : '';
      },
      rowRootClasses() {
        return {
          'tree-node-row--selected': this.isSelected,
          'tree-node-row--focused': this.isFocused,
          'tree-node-row--pinned': this.pinned,
          [`tree-node-row--${this.item.type}`]: Boolean(this.item.type),
          [`tree-node-row--level-${Math.min(this.level, 3)}`]: true
        };
      }
    },
    watch: {
      focusedNodeId: {
        handler(newVal) {
          if (newVal === this.item.id) {
            this.$nextTick(() => {
              this.$refs.treeLabel?.focus();
            });
          }
        },
        immediate: true
      }
    },
    methods: {
      toggleExpandOnly() {
        this.$emit('toggle-expand', { item: this.item });
      },
      handleNodeMouseDown(event) {
        if (!(event.shiftKey || event.ctrlKey || event.metaKey)) {
          return;
        }
        event.preventDefault();
        window.getSelection?.()?.removeAllRanges?.();
      },
      handleNodeClick(event) {
        const isMultiSelect = Boolean(event.shiftKey || event.ctrlKey || event.metaKey);
        this.$emit('node-selected', {
          item: this.item,
          isMultiSelect,
          event,
          shouldSyncMap: !isMultiSelect
        });
        this.$refs.treeLabel?.focus();
      },
      handleFocus(event) {
        this.$emit('node-focused', { item: this.item, event });
      },
      handleBlur(event) {
        this.$emit('node-blurred', { item: this.item, event });
      },
      handleKeydown(event) {
        switch (event.key) {
          case 'ArrowRight':
            if (this.hasChildren && !this.expanded) {
              event.preventDefault();
              this.$emit('toggle-expand', { item: this.item, expand: true, event });
            }
            break;
          case 'ArrowLeft':
            if (this.hasChildren && this.expanded) {
              event.preventDefault();
              this.$emit('toggle-expand', { item: this.item, expand: false, event });
            }
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.handleNodeClick(event);
            break;
          case 'ArrowUp':
          case 'ArrowDown':
            this.$emit('keyboard-navigate', {
              item: this.item,
              key: event.key,
              event
            });
            break;
          default:
            break;
        }
      },
      handleRename() {
        this.$emit('rename-requested', { item: this.item });
      },
      handleMove() {
        this.$emit('move-requested', { item: this.item });
      },
      handleDelete() {
        this.$emit('delete-requested', { item: this.item });
      }
    }
  };
</script>

<style scoped>
  .tree-node-row {
    box-sizing: border-box;
    min-height: 44px;
    border-bottom: 1px solid color-mix(in srgb, var(--v-secondary-base) 35%, transparent);
  }

  .tree-guides {
    height: 100%;
    min-height: inherit;
  }

  .tree-guide-col {
    position: relative;
    width: 14px;
    flex-shrink: 0;
  }

  .tree-guide-col::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 0;
    bottom: 0;
    border-left: 1px solid color-mix(in srgb, var(--v-secondary-base) 70%, transparent);
  }

  .tree-guide-col--last::before {
    bottom: 50%;
  }

  .tree-guide-col--last::after {
    content: '';
    position: absolute;
    left: 6px;
    top: 50%;
    width: 8px;
    border-top: 1px solid color-mix(in srgb, var(--v-secondary-base) 70%, transparent);
  }

  .tree-row-main {
    min-width: 0;
    min-height: inherit;
    padding: 0 4px 0 2px;
    cursor: pointer;
    outline: none;
    user-select: none;
    transition: background-color 0.12s ease;
  }

  .tree-node-row--level-0 .tree-row-main {
    background-color: color-mix(in srgb, var(--v-surface-base) 100%, var(--v-primary-base) 0%);
  }

  .tree-node-row--level-1 .tree-row-main {
    background-color: color-mix(in srgb, var(--v-surface-base) 97%, var(--v-text-base) 3%);
  }

  .tree-node-row--level-2 .tree-row-main,
  .tree-node-row--level-3 .tree-row-main {
    background-color: color-mix(in srgb, var(--v-surface-base) 94%, var(--v-text-base) 6%);
  }

  .tree-node-row--array .tree-row-main {
    border-left: 3px solid #ff9800;
  }

  .tree-node-row--string .tree-row-main {
    border-left: 3px solid #1e88e5;
    padding-left: 8px;
  }

  .tree-node-row--module .tree-row-main {
    border-left: 3px solid #00897b;
  }

  .tree-row-main:hover {
    background-color: color-mix(
      in srgb,
      var(--v-primary-base) 8%,
      var(--v-surface-base)
    ) !important;
  }

  .tree-node-row--selected .tree-row-main {
    background-color: color-mix(
      in srgb,
      var(--v-primary-base) 20%,
      var(--v-surface-base)
    ) !important;
  }

  .tree-node-row--selected .tree-row-name--array,
  .tree-node-row--selected .tree-row-name--string {
    color: var(--v-primary-base);
    font-weight: 600;
  }

  .tree-node-row--focused .tree-row-main {
    box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--v-info-base) 50%, transparent);
  }

  .tree-chevron-slot {
    width: 36px;
  }

  .tree-expand-btn {
    margin: 0;
  }

  .tree-chevron--open {
    transform: rotate(90deg);
    transition: transform 0.15s ease;
  }

  .tree-type-icon {
    opacity: 0.95;
  }

  .tree-type-icon--module {
    opacity: 0.85;
  }

  .tree-type-chip {
    height: 20px !important;
    font-size: 11px !important;
    padding: 0 6px !important;
    opacity: 0.85;
  }

  .tree-row-name {
    line-height: 1.35;
    font-size: 13px;
  }

  .tree-row-name--array {
    font-size: 13px;
  }

  .tree-row-name--string {
    font-size: 13px;
  }

  .tree-row-name--module {
    font-size: 13px;
    font-weight: 400;
  }

  .tree-row-text {
    min-width: 0;
    padding-right: 4px;
  }

  .tree-row-actions {
    gap: 0;
    opacity: 1;
    flex-shrink: 0;
  }

  .tree-action-btn {
    opacity: 0.7;
    transition: opacity 0.1s ease;
  }

  .tree-action-btn:hover,
  .tree-action-btn:focus {
    opacity: 1;
  }

  .tree-node-row--selected .tree-action-btn {
    opacity: 0.65;
  }

  .tree-node-row--selected .tree-action-btn:hover,
  .tree-node-row--selected .tree-action-btn:focus {
    opacity: 1;
  }
</style>
